import { inject, Injectable, signal } from '@angular/core';
import { catchError, defer, finalize, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';

import { resolveCatalogErrorMessage } from '../catalog-error';
import { VariantsApiService } from '../api/variants-api.service';
import type { ProductVariantDto } from '../models/variant.model';
import { InventoryService } from '../../inventory/inventory.service';
import type { ChangeCostPriceRequest, ChangeStatusRequest, InventoryUnitDto, PatchInventoryUnitRequest, RegisterUnitRequest, SellUnitRequest } from '../../inventory/models/inventory.model';

export type ProductUnitUpdateRequest = {
  variantId?: string;
  storeId?: string;
  supplierId?: string | null;
  imei?: string | null;
  serialNumber?: string | null;
  receivedAt?: string | null;
  batteryHealth?: number | null;
  price?: number;
  discountPrice?: number | null;
  hasBox?: boolean;
  isRefurbished?: boolean;
  observation?: string | null;
  status?: InventoryUnitDto['status'];
  changeReason?: string;
};

@Injectable({ providedIn: 'root' })
export class ProductUnitsStore {
  private readonly variantsApi = inject(VariantsApiService);
  private readonly inventoryService = inject(InventoryService);

  readonly units = signal<InventoryUnitDto[]>([]);
  readonly variants = signal<ProductVariantDto[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  reset(): void {
    this.units.set([]);
    this.variants.set([]);
    this.loading.set(false);
    this.error.set(null);
  }

  load(productId: string): void {
    this.units.set([]);
    this.variants.set([]);
    this.loading.set(true);
    this.error.set(null);

    this.variantsApi
      .list(productId)
      .pipe(
        switchMap((response) => {
          const variants = response.data;

          this.variants.set(variants);

          if (variants.length === 0) {
            return of([] as InventoryUnitDto[]);
          }

          return forkJoin(
            variants.map((variant) =>
              this.inventoryService.listUnits({ variantId: variant.id, page: 1, limit: 100 }).pipe(map((result) => result.data))
            )
          ).pipe(map((groups) => groups.flat()));
        }),
        map((units) => sortUnitsByRecency(units))
      )
      .subscribe({
        next: (units) => {
          this.units.set(units);
          this.loading.set(false);
        },
        error: (error: unknown) => {
          this.error.set(resolveErrorMessage(error));
          this.loading.set(false);
        }
      });
  }

  create(body: RegisterUnitRequest): Observable<InventoryUnitDto> {
    this.loading.set(true);
    this.error.set(null);

    return this.inventoryService.registerUnit(body).pipe(
      map((response) => response.data),
      tap((unit) => {
        this.units.update((current) => sortUnitsByRecency([unit, ...current]));
      }),
      finalize(() => this.loading.set(false)),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  update(current: InventoryUnitDto, body: ProductUnitUpdateRequest): Observable<InventoryUnitDto> {
    this.loading.set(true);
    this.error.set(null);

    if (body.status !== undefined && body.status === current.status) {
      this.loading.set(false);
      return throwError(() => new Error('No changes detected: current and requested status are equal.'));
    }

    const patch = buildPatchRequest(current, body);

    if (patch === null) {
      this.loading.set(false);
      return throwError(() => new Error('No detectamos cambios para guardar.'));
    }

    return this.inventoryService.patchUnit(current.id, patch).pipe(
      map((response) => response.data),
      tap((unit) => this.upsert(unit)),
      finalize(() => this.loading.set(false)),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  changeStatus(unitId: string, body: ChangeStatusRequest): Observable<InventoryUnitDto> {
    this.loading.set(true);
    this.error.set(null);

    const normalizedReason = body.reason?.trim() ? body.reason.trim() : undefined;
    const requiresReason = body.status === 'MISSING' || body.status === 'SOLD' || body.status === 'IN_REPAIR';

    if (requiresReason && !normalizedReason) {
      this.loading.set(false);
      this.error.set('Necesitás un motivo para transiciones hacia MISSING, SOLD o IN_REPAIR.');
      return throwError(() => new Error('Reason required for status transition.'));
    }

    const payload: ChangeStatusRequest = {
      status: body.status,
      ...(normalizedReason !== undefined ? { reason: normalizedReason } : {})
    };

    return this.inventoryService.changeStatus(unitId, payload).pipe(
      map((response) => response.data),
      tap((unit) => this.upsert(unit)),
      finalize(() => this.loading.set(false)),
      catchError((error: unknown) => {
        this.error.set(resolveCatalogErrorMessage(error, 'No pudimos completar la operación sobre unidades.'));
        return throwError(() => error);
      })
    );
  }

  sellUnit(unitId: string, body: SellUnitRequest): Observable<InventoryUnitDto> {
    this.loading.set(true);
    this.error.set(null);

    const normalizedReason = body.reason?.trim() ? body.reason.trim() : undefined;

    const payload: PatchInventoryUnitRequest = {
      status: 'SOLD',
      saleChannel: body.saleChannel,
      ...(normalizedReason !== undefined ? { reason: normalizedReason } : {})
    };

    return this.inventoryService.patchUnit(unitId, payload).pipe(
      map((response) => response.data),
      tap((unit) => this.upsert(unit)),
      finalize(() => this.loading.set(false)),
      catchError((error: unknown) => {
        this.error.set(resolveCatalogErrorMessage(error, 'No pudimos registrar la venta de la unidad.'));
        return throwError(() => error);
      })
    );
  }

  changeCostPrice(unitId: string, body: ChangeCostPriceRequest): Observable<InventoryUnitDto> {
    this.loading.set(true);
    this.error.set(null);

    return this.inventoryService.changeCostPrice(unitId, body).pipe(
      map((response) => response.data),
      tap((unit) => this.upsert(unit)),
      finalize(() => this.loading.set(false)),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  /** Soft-deletes the unit and drops it from the loaded list (the server hides it from lists). */
  delete(unitId: string): Observable<{ id: string; deletedAt: string | null }> {
    this.loading.set(true);
    this.error.set(null);

    return this.inventoryService.deleteUnit(unitId).pipe(
      map((response) => response.data),
      tap(() => {
        this.units.update((current) => current.filter((unit) => unit.id !== unitId));
      }),
      finalize(() => this.loading.set(false)),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  private upsert(unit: InventoryUnitDto): void {
    this.units.update((current) => {
      const next = current.some((item) => item.id === unit.id)
        ? current.map((item) => (item.id === unit.id ? unit : item))
        : [unit, ...current];

      return sortUnitsByRecency(next);
    });
  }
}

/**
 * Builds a patch request from current state and update body.
 * @returns null when no fields differ; caller must throw on null.
 */
const buildPatchRequest = (current: InventoryUnitDto, body: ProductUnitUpdateRequest): PatchInventoryUnitRequest | null => {
  const nextPatch: PatchInventoryUnitRequest = {};
  const nextVariantId = body.variantId;
  const nextStoreId = body.storeId;
  const nextSupplierId = normalizeOptionalSelect(body.supplierId);
  const nextImei = normalizeOptionalText(body.imei);
  const nextSerialNumber = normalizeOptionalText(body.serialNumber);
  const nextReceivedAt = normalizeOptionalDate(body.receivedAt);
  const nextBatteryHealth = normalizeOptionalNumber(body.batteryHealth);
  const nextDiscountPrice = normalizeOptionalNumber(body.discountPrice);
  const reason = body.changeReason?.trim();

  if (nextVariantId !== undefined && nextVariantId !== current.variantId) {
    nextPatch.variantId = nextVariantId;
  }

  if (nextStoreId !== undefined && nextStoreId !== current.storeId) {
    nextPatch.storeId = nextStoreId;
  }

  if (body.supplierId !== undefined && nextSupplierId !== current.supplierId) {
    nextPatch.supplierId = nextSupplierId;
  }

  if (body.imei !== undefined && nextImei !== (current.imei ?? null)) {
    nextPatch.imei = nextImei;
  }

  if (body.serialNumber !== undefined && nextSerialNumber !== (current.serialNumber ?? null)) {
    nextPatch.serialNumber = nextSerialNumber;
  }

  if (body.receivedAt !== undefined && nextReceivedAt !== current.receivedAt) {
    nextPatch.receivedAt = nextReceivedAt;
  }

  if (body.batteryHealth !== undefined && nextBatteryHealth !== current.batteryHealth) {
    nextPatch.batteryHealth = nextBatteryHealth;
  }

  if (body.price !== undefined && body.price !== current.price) {
    nextPatch.price = body.price;
  }

  if (body.discountPrice !== undefined && nextDiscountPrice !== current.discountPrice) {
    nextPatch.discountPrice = nextDiscountPrice;
  }

  if (body.hasBox !== undefined && body.hasBox !== current.hasBox) {
    nextPatch.hasBox = body.hasBox;
  }

  if (body.isRefurbished !== undefined && body.isRefurbished !== current.isRefurbished) {
    nextPatch.isRefurbished = body.isRefurbished;
  }

  const nextObservation = normalizeOptionalText(body.observation);

  if (body.observation !== undefined && nextObservation !== (current.observation ?? null)) {
    nextPatch.observation = nextObservation;
  }

  if (body.status !== undefined && body.status !== current.status) {
    nextPatch.status = body.status;
  }

  if (Object.keys(nextPatch).length === 0) {
    return null;
  }

  if (reason) {
    nextPatch.reason = reason;
  }

  return nextPatch;
};

const normalizeOptionalText = (value: string | null | undefined): string | null => {
  const normalized = value?.trim() ?? '';
  return normalized ? normalized : null;
};

const normalizeOptionalSelect = (value: string | null | undefined): string | null => {
  const normalized = value?.trim() ?? '';
  return normalized ? normalized : null;
};

const normalizeOptionalDate = (value: string | null | undefined): string | null => {
  const normalized = value?.trim() ?? '';
  return normalized ? new Date(normalized).toISOString() : null;
};

const normalizeOptionalNumber = (value: number | null | undefined): number | null => {
  return value === undefined ? null : value;
};

const sortUnitsByRecency = (units: InventoryUnitDto[]): InventoryUnitDto[] => {
  return [...units].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
};

const resolveErrorMessage = (error: unknown): string => {
  return resolveCatalogErrorMessage(error, 'No pudimos completar la operación sobre unidades.');
};
